import * as React from 'react';
import {
    Alert, Avatar, Box, Button, Card, CardContent, Chip, Collapse, IconButton, InputAdornment, MenuItem, Stack, Tab,
    Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup, Tooltip,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
    BETUP_FEED_LABELS, BetupFeedPeriod, getBetupFeedChannels, getBetupFeedChannelSubscriberEvents,
    getBetupFeedChannelSubscribers, getBetupFeedChannelSubscriberStats,
} from '../Api';
import {
    BetupFeedChannelDto, BetupFeedChannelSubscriberBreakdownDto, BetupFeedChannelSubscriberDto,
    BetupFeedChannelSubscriberEventDto, BetupFeedChannelSubscriberEventKind, BetupFeedChannelSubscriberStatsDto,
} from '../models/dtos/BetupFeedChannelDto';
import { BetupFeedPlayerDto } from '../models/dtos/BetupFeedPostDto';
import { formatCount, formatFeedDate, loadErrorText } from '../components/betupFeed/BetupFeedShared';
import { ChannelLogo, channelName, TopChannelChips } from '../components/betupFeed/BetupFeedChannelDialogs';
import ChannelSubscriberChart, {
    longDay, shortDay, SUBSCRIBED_COLOR, UNSUBSCRIBED_COLOR,
} from '../components/betupFeed/ChannelSubscriberChart';
import ChannelSubscriberDrawer, {
    languageLabel, platformLabel, relativeTime, SubscriberDrawerEntry,
} from '../components/betupFeed/ChannelSubscriberDrawer';

const PRESETS = [
    { key: '7d', label: '7 days', days: 7 },
    { key: '30d', label: '30 days', days: 30 },
    { key: '90d', label: '90 days', days: 90 },
    { key: '1y', label: '12 months', days: 365 },
] as const;
const DEFAULT_PRESET = '30d';
const PAGE_SIZE = 25;
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

type ListTab = 'subscribers' | 'activity';

/** yyyy-MM-dd of "now" in [tz]. */
function todayIn(tz: string): string {
    try {
        return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    } catch {
        return new Date().toISOString().slice(0, 10);
    }
}

function addDays(day: string, delta: number): string {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().slice(0, 10);
}

function presetPeriod(days: number, tz: string): { from: string; to: string } {
    const to = todayIn(tz);
    return { from: addDays(to, -(days - 1)), to };
}

function percent(part: number, whole: number): string {
    if (whole <= 0) return '—';
    return `${Math.round((part / whole) * 100)}%`;
}

function signed(value: number): string {
    return value > 0 ? `+${formatCount(value)}` : value < 0 ? `−${formatCount(-value)}` : '0';
}

/**
 * BETUP Feed › Channels › a channel's subscribers: a period's subscribes / unsubscribes / totals (pick a day on the
 * chart to list its players), who subscribed by country / platform / language, the current subscribers newest first
 * and the subscribe log — a click on a player opens their card beside the list (↑ / ↓ walk it).
 */
export default function BetupFeedChannelSubscribersPage() {
    const { id } = useParams();
    const channelId = Number(id);
    const [searchParams, setSearchParams] = useSearchParams();

    const tz = searchParams.get('tz') || BROWSER_TZ;
    const presetKey = searchParams.get('from') ? null : (searchParams.get('preset') || DEFAULT_PRESET);
    const preset = PRESETS.find(p => p.key === presetKey) ?? null;
    const fromPreset = preset ? presetPeriod(preset.days, tz) : null;
    const from = fromPreset?.from ?? searchParams.get('from') ?? presetPeriod(30, tz).from;
    const to = fromPreset?.to ?? searchParams.get('to') ?? todayIn(tz);
    const tab: ListTab = searchParams.get('tab') === 'activity' ? 'activity' : 'subscribers';
    const day = searchParams.get('day');
    const kind = (searchParams.get('kind') as BetupFeedChannelSubscriberEventKind) || 'all';
    const query = searchParams.get('q') ?? '';
    const period: BetupFeedPeriod = React.useMemo(() => ({ from, to, tz }), [from, to, tz]);

    const update = React.useCallback((changes: Record<string, string | null>) => {
        setSearchParams(current => {
            const next = new URLSearchParams(current);
            Object.entries(changes).forEach(([key, value]) => value == null ? next.delete(key) : next.set(key, value));
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const [channel, setChannel] = React.useState<BetupFeedChannelDto | null>(null);
    const [stats, setStats] = React.useState<BetupFeedChannelSubscriberStatsDto | null>(null);
    const [statsLoading, setStatsLoading] = React.useState(false);
    const [statsError, setStatsError] = React.useState<string | null>(null);
    const [showTable, setShowTable] = React.useState(false);
    const [reload, setReload] = React.useState(0);

    React.useEffect(() => {
        getBetupFeedChannels().then(list => setChannel(list?.find(c => c.id === channelId) ?? null));
    }, [channelId]);

    React.useEffect(() => {
        let cancelled = false;
        setStatsLoading(true);
        getBetupFeedChannelSubscriberStats(channelId, period).then(result => {
            if (cancelled) return;
            setStatsLoading(false);
            setStats(result);
            setStatsError(result == null ? loadErrorText(BETUP_FEED_LABELS.channelSubscriberStats, 'Failed to load the statistics.') : null);
        });
        return () => { cancelled = true; };
    }, [channelId, period, reload]);

    // ---- The lists ----
    const [subscribers, setSubscribers] = React.useState<BetupFeedChannelSubscriberDto[]>([]);
    const [subscribersTotal, setSubscribersTotal] = React.useState(0);
    const [subscribersPage, setSubscribersPage] = React.useState<GridPaginationModel>({ page: 0, pageSize: PAGE_SIZE });
    const [events, setEvents] = React.useState<BetupFeedChannelSubscriberEventDto[]>([]);
    const [eventsTotal, setEventsTotal] = React.useState(0);
    const [eventsPage, setEventsPage] = React.useState<GridPaginationModel>({ page: 0, pageSize: PAGE_SIZE });
    const [listLoading, setListLoading] = React.useState(false);
    const [listError, setListError] = React.useState<string | null>(null);
    const [searchText, setSearchText] = React.useState(query);

    // The drawer: the row's index in the visible list; after a page turn, which end to land on.
    const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);
    const pendingSelection = React.useRef<'first' | 'last' | null>(null);
    const deepLinkedUser = React.useRef<number | null>(Number(searchParams.get('user')) || null);

    React.useEffect(() => {
        const handle = setTimeout(() => {
            if (searchText.trim() !== query) {
                update({ q: searchText.trim() || null });
                setSubscribersPage(p => ({ ...p, page: 0 }));
            }
        }, 350);
        return () => clearTimeout(handle);
    }, [searchText, query, update]);

    const eventsPeriod: BetupFeedPeriod = React.useMemo(
        () => (day ? { from: day, to: day, tz } : period), [day, tz, period],
    );

    React.useEffect(() => { setEventsPage(p => ({ ...p, page: 0 })); }, [eventsPeriod, kind]);

    React.useEffect(() => {
        if (tab !== 'subscribers') return;
        let cancelled = false;
        setListLoading(true);
        getBetupFeedChannelSubscribers(channelId, query, subscribersPage.page * subscribersPage.pageSize, subscribersPage.pageSize)
            .then(result => {
                if (cancelled) return;
                setListLoading(false);
                setListError(result == null ? loadErrorText(BETUP_FEED_LABELS.channelSubscribers, 'Failed to load the subscribers.') : null);
                setSubscribers(result?.items ?? []);
                setSubscribersTotal(result?.totalItems ?? 0);
                landPendingSelection(result?.items.length ?? 0);
            });
        return () => { cancelled = true; };
    }, [channelId, tab, query, subscribersPage, reload]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (tab !== 'activity') return;
        let cancelled = false;
        setListLoading(true);
        getBetupFeedChannelSubscriberEvents(channelId, eventsPeriod, kind, eventsPage.page * eventsPage.pageSize, eventsPage.pageSize)
            .then(result => {
                if (cancelled) return;
                setListLoading(false);
                setListError(result == null ? loadErrorText(BETUP_FEED_LABELS.channelSubscriberEvents, 'Failed to load the activity.') : null);
                setEvents(result?.items ?? []);
                setEventsTotal(result?.totalItems ?? 0);
                landPendingSelection(result?.items.length ?? 0);
            });
        return () => { cancelled = true; };
    }, [channelId, tab, eventsPeriod, kind, eventsPage, reload]); // eslint-disable-line react-hooks/exhaustive-deps

    function landPendingSelection(count: number) {
        const pending = pendingSelection.current;
        pendingSelection.current = null;
        if (pending == null) return;
        setSelectedIndex(count === 0 ? null : pending === 'first' ? 0 : count - 1);
    }

    // ---- Drawer navigation over the visible list ----
    const rows = tab === 'subscribers' ? subscribers : events;
    const pageModel = tab === 'subscribers' ? subscribersPage : eventsPage;
    const setPageModel = tab === 'subscribers' ? setSubscribersPage : setEventsPage;
    const total = tab === 'subscribers' ? subscribersTotal : eventsTotal;
    const absoluteIndex = selectedIndex != null ? pageModel.page * pageModel.pageSize + selectedIndex : null;

    const entry: SubscriberDrawerEntry | null = React.useMemo(() => {
        if (selectedIndex == null) {
            const userId = deepLinkedUser.current;
            return userId ? { userId, player: null } : null;
        }
        if (tab === 'subscribers') {
            const row = subscribers[selectedIndex];
            if (!row) return null;
            return {
                userId: row.user.id, player: row.user, subscribedAt: row.subscribed_at,
                notificationsEnabled: row.notifications_enabled, channelsCount: row.channels_count, subscribedNow: true,
            };
        }
        const row = events[selectedIndex];
        if (!row) return null;
        return {
            userId: row.user_id, player: row.user, subscribedNow: row.subscribed_now,
            event: { subscribed: row.subscribed, at: row.at, backfilled: row.backfilled },
        };
    }, [selectedIndex, tab, subscribers, events]);

    React.useEffect(() => {
        const current = searchParams.get('user');
        const wanted = entry ? String(entry.userId) : null;
        if (current !== wanted) update({ user: wanted });
    }, [entry]); // eslint-disable-line react-hooks/exhaustive-deps

    const select = (index: number | null) => { deepLinkedUser.current = null; setSelectedIndex(index); };
    const goNext = absoluteIndex != null && absoluteIndex + 1 < total ? () => {
        if (selectedIndex! + 1 < rows.length) select(selectedIndex! + 1);
        else { pendingSelection.current = 'first'; setPageModel(p => ({ ...p, page: p.page + 1 })); }
    } : null;
    const goPrevious = absoluteIndex != null && absoluteIndex > 0 ? () => {
        if (selectedIndex! > 0) select(selectedIndex! - 1);
        else { pendingSelection.current = 'last'; setPageModel(p => ({ ...p, page: p.page - 1 })); }
    } : null;
    const closeDrawer = React.useCallback(() => { deepLinkedUser.current = null; setSelectedIndex(null); }, []);

    const switchTab = (next: ListTab) => { closeDrawer(); update({ tab: next === 'subscribers' ? null : next }); };
    const pickDay = (picked: string | null) => {
        closeDrawer();
        update({ day: picked, tab: picked ? 'activity' : (tab === 'subscribers' ? null : tab) });
    };
    const setPreset = (key: string) => { closeDrawer(); update({ preset: key === DEFAULT_PRESET ? null : key, from: null, to: null, day: null }); };
    const setCustom = (nextFrom: string, nextTo: string) => {
        if (!nextFrom || !nextTo) return;
        closeDrawer();
        update({ preset: null, from: nextFrom, to: nextTo, day: null });
    };

    // ---- Columns ----
    const subscriberColumns: GridColDef<BetupFeedChannelSubscriberDto>[] = [
        { field: 'user', headerName: 'Player', flex: 1, minWidth: 220, sortable: false, renderCell: ({ row }) => <PlayerCell player={row.user} /> },
        {
            field: 'subscribed_at', headerName: 'Subscribed', width: 190, sortable: false,
            renderCell: ({ row }) => <TimeCell value={row.subscribed_at} />,
        },
        {
            field: 'notifications_enabled', headerName: 'Push', width: 90, sortable: false,
            renderCell: ({ row }) => row.notifications_enabled
                ? <Chip size="small" variant="outlined" color="success" label="On" />
                : <Chip size="small" variant="outlined" color="warning" label="Muted" />,
        },
        { field: 'platform', headerName: 'Platform', width: 100, sortable: false, valueGetter: ({ row }) => platformLabel(row.platform) },
        { field: 'language', headerName: 'Language', width: 110, sortable: false, valueGetter: ({ row }) => languageLabel(row.language) },
        { field: 'channels_count', headerName: 'Channels', type: 'number', width: 90, sortable: false },
        {
            field: 'last_visit', headerName: 'Last visit', width: 150, sortable: false,
            renderCell: ({ row }) => <Typography variant="body2" color="text.secondary">{relativeTime(row.last_visit) || '—'}</Typography>,
        },
    ];

    const eventColumns: GridColDef<BetupFeedChannelSubscriberEventDto & { key: string }>[] = [
        {
            field: 'user', headerName: 'Player', flex: 1, minWidth: 220, sortable: false,
            renderCell: ({ row }) => <PlayerCell player={row.user} fallbackId={row.user_id} />,
        },
        {
            field: 'subscribed', headerName: 'Action', width: 160, sortable: false,
            renderCell: ({ row }) => (
                <Stack direction="row" gap={1} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: row.subscribed ? SUBSCRIBED_COLOR : UNSUBSCRIBED_COLOR }} />
                    <Typography variant="body2">{row.subscribed ? 'Subscribed' : 'Unsubscribed'}</Typography>
                    {row.backfilled && (
                        <Tooltip title="This subscription existed before the history was recorded; its time is the subscribe time.">
                            <Chip size="small" variant="outlined" label="earlier" />
                        </Tooltip>
                    )}
                </Stack>
            ),
        },
        { field: 'at', headerName: 'When', width: 190, sortable: false, renderCell: ({ row }) => <TimeCell value={row.at} /> },
        {
            field: 'subscribed_now', headerName: 'Now', width: 140, sortable: false,
            renderCell: ({ row }) => row.subscribed_now
                ? <Chip size="small" color="success" label="Subscribed" />
                : <Chip size="small" variant="outlined" label="Not subscribed" />,
        },
    ];

    const s = stats;
    const recordedSince = s?.recorded_since ? new Date(s.recorded_since) : null;
    const historyPartial = s != null && (recordedSince == null || recordedSince.toISOString().slice(0, 10) > s.from);

    return (
        <Stack gap={2} sx={{ mx: { xs: 2, md: 5 }, my: 4, pr: entry ? { sm: '420px' } : 0, transition: 'padding 200ms' }}>
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                <IconButton component={RouterLink} to={`/marketing/betup-feed/channels/${channelId}`} aria-label="Back to the channel">
                    <ArrowBackIcon />
                </IconButton>
                {channel && <ChannelLogo channel={channel} />}
                <Stack sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="h4">{channel ? channelName(channel) : `Channel #${channelId}`} — subscribers</Typography>
                    {channel && (
                        <Stack direction="row" gap={1} alignItems="center" sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">#{channel.id}</Typography>
                            {channel.active ? <Chip size="small" color="success" label="Active" /> : <Chip size="small" variant="outlined" label="Inactive" />}
                            <TopChannelChips channel={channel} />
                        </Stack>
                    )}
                </Stack>
                <Button variant="outlined" component={RouterLink} to={`/marketing/betup-feed/channels?posts=${channelId}`}>Posts</Button>
            </Stack>

            {/* Filters: one row above the charts. */}
            <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                <ToggleButtonGroup size="small" exclusive value={preset?.key ?? null} onChange={(_, key) => key && setPreset(key)}>
                    {PRESETS.map(p => <ToggleButton key={p.key} value={p.key}>{p.label}</ToggleButton>)}
                </ToggleButtonGroup>
                <TextField size="small" type="date" label="From" value={from} InputLabelProps={{ shrink: true }}
                    inputProps={{ max: to }} onChange={e => setCustom(e.target.value, to)} />
                <TextField size="small" type="date" label="To" value={to} InputLabelProps={{ shrink: true }}
                    inputProps={{ min: from }} onChange={e => setCustom(from, e.target.value)} />
                <TextField size="small" select label="Days in" value={tz} sx={{ minWidth: 170 }}
                    onChange={e => update({ tz: e.target.value === BROWSER_TZ ? null : e.target.value, day: null })}>
                    <MenuItem value={BROWSER_TZ}>{BROWSER_TZ} (yours)</MenuItem>
                    {BROWSER_TZ !== 'UTC' && <MenuItem value="UTC">UTC</MenuItem>}
                </TextField>
                <Tooltip title="Reload">
                    <IconButton onClick={() => setReload(r => r + 1)}><RefreshIcon /></IconButton>
                </Tooltip>
            </Stack>

            {statsError && <Alert severity="error" onClose={() => setStatsError(null)}>{statsError}</Alert>}

            {/* KPI tiles */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, opacity: statsLoading ? 0.6 : 1 }}>
                <StatTile label="Subscribers now" value={formatCount(s?.subscribers_now)}
                    detail={s ? `${percent(s.notifications_enabled_now, s.subscribers_now)} get pushes` : ''} />
                <StatTile label="Net change" value={s ? signed(s.net) : '—'}
                    detail={s ? `${formatCount(s.total_at_start)} → ${formatCount(s.total_at_end)}` : ''} />
                <StatTile label="Subscribed" value={formatCount(s?.subscribed)} accent={SUBSCRIBED_COLOR}
                    detail={s ? `${formatCount(s.new_subscribers)} players` : ''} />
                <StatTile label="Unsubscribed" value={formatCount(s?.unsubscribed)} accent={UNSUBSCRIBED_COLOR}
                    detail={s ? `${percent(s.unsubscribed, s.total_at_start + s.subscribed)} of those reached` : ''} />
                <StatTile label="Still subscribed" value={s ? percent(s.still_subscribed, s.new_subscribers) : '—'}
                    detail={s ? `${formatCount(s.still_subscribed)} of ${formatCount(s.new_subscribers)} new players` : ''} />
            </Box>

            <Card variant="outlined">
                <CardContent>
                    <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                        <Typography variant="h6" sx={{ flex: 1 }}>Per day</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {longDay(from)} – {longDay(to)} · days in {tz}
                        </Typography>
                        <Button size="small" onClick={() => setShowTable(v => !v)}>{showTable ? 'Hide table' : 'Show as table'}</Button>
                    </Stack>
                    {historyPartial && (
                        <Alert severity="info" sx={{ mb: 1.5 }}>
                            Unsubscribes are recorded {recordedSince ? `since ${recordedSince.toLocaleDateString()}` : 'from the next one on'}.
                            Before that only players who were still subscribed show as subscribes; the totals are exact.
                        </Alert>
                    )}
                    {s && s.days.length > 0
                        ? <ChannelSubscriberChart days={s.days} selectedDay={day} onSelect={pickDay} />
                        : <Typography variant="body2" color="text.secondary">{statsLoading ? 'Loading…' : 'No data.'}</Typography>}
                    <Collapse in={showTable && s != null} unmountOnExit>
                        <Box sx={{ maxHeight: 320, overflowY: 'auto', mt: 2 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Day</TableCell>
                                        <TableCell align="right">Subscribed</TableCell>
                                        <TableCell align="right">Unsubscribed</TableCell>
                                        <TableCell align="right">Net</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[...(s?.days ?? [])].reverse().map(d => (
                                        <TableRow key={d.date} hover selected={d.date === day} sx={{ cursor: 'pointer' }}
                                            onClick={() => pickDay(d.date === day ? null : d.date)}>
                                            <TableCell>{longDay(d.date)}</TableCell>
                                            <TableCell align="right">{formatCount(d.subscribed)}</TableCell>
                                            <TableCell align="right">{formatCount(d.unsubscribed)}</TableCell>
                                            <TableCell align="right">{signed(d.subscribed - d.unsubscribed)}</TableCell>
                                            <TableCell align="right">{formatCount(d.total)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </CardContent>
            </Card>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
                <Breakdown title="New subscribers by country" items={s?.countries} total={s?.new_subscribers ?? 0}
                    label={item => item.label ?? item.key ?? 'Unknown'} />
                <Breakdown title="By platform" items={s?.platforms} total={s?.new_subscribers ?? 0}
                    label={item => platformLabel(item.key)} />
                <Breakdown title="By app language" items={s?.languages} total={s?.new_subscribers ?? 0}
                    label={item => languageLabel(item.key)} />
            </Box>

            <Card variant="outlined">
                <Tabs value={tab} onChange={(_, next) => switchTab(next)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Tab value="subscribers" label={`Latest subscribers${s ? ` (${formatCount(s.subscribers_now)})` : ''}`} />
                    <Tab value="activity" label="Subscribe activity" />
                </Tabs>
                <CardContent>
                    <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
                        {tab === 'subscribers' ? (
                            <TextField size="small" placeholder="Name or user id" value={searchText}
                                onChange={e => setSearchText(e.target.value)} sx={{ minWidth: 260 }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                        ) : (
                            <>
                                <ToggleButtonGroup size="small" exclusive value={kind}
                                    onChange={(_, next) => next && update({ kind: next === 'all' ? null : next })}>
                                    <ToggleButton value="all">All</ToggleButton>
                                    <ToggleButton value="subscribed">Subscribed</ToggleButton>
                                    <ToggleButton value="unsubscribed">Unsubscribed</ToggleButton>
                                </ToggleButtonGroup>
                                {day
                                    ? <Chip color="primary" label={`Day: ${shortDay(day)}`} onDelete={() => pickDay(null)} />
                                    : <Typography variant="body2" color="text.secondary">Whole period — pick a day on the chart to narrow it</Typography>}
                            </>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="caption" color="text.secondary">Click a player for details · ↑ ↓ to move</Typography>
                    </Stack>
                    {listError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setListError(null)}>{listError}</Alert>}
                    {tab === 'subscribers' ? (
                        <DataGrid
                            autoHeight
                            rows={subscribers}
                            getRowId={row => row.user.id}
                            columns={subscriberColumns}
                            loading={listLoading}
                            rowCount={subscribersTotal}
                            paginationMode="server"
                            paginationModel={subscribersPage}
                            onPaginationModelChange={model => { closeDrawer(); setSubscribersPage(model); }}
                            pageSizeOptions={[PAGE_SIZE, 50, 100]}
                            onRowClick={params => select(subscribers.findIndex(r => r.user.id === params.id))}
                            getRowClassName={params => (entry && tab === 'subscribers' && params.id === entry.userId ? 'selected-row' : '')}
                            disableColumnMenu
                            disableRowSelectionOnClick
                            sx={gridSx}
                            localeText={{ noRowsLabel: query ? 'Nobody matches' : 'No subscribers yet' }}
                        />
                    ) : (
                        <DataGrid
                            autoHeight
                            rows={events.map((row, index) => ({ ...row, key: `${eventsPage.page}-${index}` }))}
                            getRowId={row => row.key}
                            columns={eventColumns}
                            loading={listLoading}
                            rowCount={eventsTotal}
                            paginationMode="server"
                            paginationModel={eventsPage}
                            onPaginationModelChange={model => { closeDrawer(); setEventsPage(model); }}
                            pageSizeOptions={[PAGE_SIZE, 50, 100]}
                            onRowClick={params => select(Number(String(params.id).split('-')[1]))}
                            getRowClassName={params => (selectedIndex != null && params.id === `${eventsPage.page}-${selectedIndex}` ? 'selected-row' : '')}
                            disableColumnMenu
                            disableRowSelectionOnClick
                            sx={gridSx}
                            localeText={{ noRowsLabel: day ? `Nothing on ${shortDay(day)}` : 'Nothing in this period' }}
                        />
                    )}
                </CardContent>
            </Card>

            <ChannelSubscriberDrawer
                entry={entry}
                position={absoluteIndex != null ? `${formatCount(absoluteIndex + 1)} of ${formatCount(total)}` : null}
                onClose={closeDrawer}
                onPrevious={goPrevious}
                onNext={goNext}
            />
        </Stack>
    );
}

const gridSx = {
    '& .MuiDataGrid-row': { cursor: 'pointer' },
    '& .selected-row': { bgcolor: 'rgba(9, 137, 227, 0.10)' },
    '& .selected-row:hover': { bgcolor: 'rgba(9, 137, 227, 0.16)' },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
};

function StatTile({ label, value, detail, accent }: { label: string; value: string; detail?: string; accent?: string }) {
    return (
        <Card variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" gap={0.75} alignItems="center">
                    {accent && <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: accent }} />}
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 600, my: 0.25 }}>{value}</Typography>
                <Typography variant="caption" color="text.secondary">{detail || ' '}</Typography>
            </CardContent>
        </Card>
    );
}

function Breakdown({ title, items, total, label }: {
    title: string;
    items: BetupFeedChannelSubscriberBreakdownDto[] | undefined;
    total: number;
    label: (item: BetupFeedChannelSubscriberBreakdownDto) => string;
}) {
    const max = Math.max(1, ...(items ?? []).map(i => i.count));
    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
                {(items?.length ?? 0) === 0 && <Typography variant="body2" color="text.secondary">Nobody subscribed in this period.</Typography>}
                <Stack gap={1}>
                    {(items ?? []).map(item => (
                        <Box key={item.key ?? 'unknown'}>
                            <Stack direction="row" justifyContent="space-between" gap={1}>
                                <Typography variant="body2" noWrap>{label(item)}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {formatCount(item.count)} · {percent(item.count, total)}
                                </Typography>
                            </Stack>
                            <Box sx={{ height: 6, bgcolor: '#f0efec', borderRadius: 1, mt: 0.5 }}>
                                <Box sx={{ height: 6, width: `${(item.count / max) * 100}%`, bgcolor: SUBSCRIBED_COLOR, borderRadius: 1 }} />
                            </Box>
                        </Box>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
}

function PlayerCell({ player, fallbackId }: { player: BetupFeedPlayerDto | null; fallbackId?: number }) {
    const name = player?.name || `#${player?.id ?? fallbackId}`;
    return (
        <Stack direction="row" gap={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar src={player?.photo_url ?? undefined} alt={name} sx={{ width: 32, height: 32 }}>{name.slice(0, 1).toUpperCase()}</Avatar>
            <Stack sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{player ? name : 'Deleted player'}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                    #{player?.id ?? fallbackId}{player?.level != null ? ` · Lv ${player.level}` : ''}{player?.country_iso ? ` · ${player.country_iso}` : ''}
                </Typography>
            </Stack>
        </Stack>
    );
}

function TimeCell({ value }: { value: string | null }) {
    return (
        <Stack>
            <Typography variant="body2">{relativeTime(value) || '—'}</Typography>
            <Typography variant="caption" color="text.secondary">{formatFeedDate(value)}</Typography>
        </Stack>
    );
}
